import features from 'src/replicant/ruleset/features';
import { State } from 'src/replicant/State';
import * as Gifts from '../../replicant/gifts';
import { isMatch2Available } from 'src/replicant/match2/match2.getters';
import { getJSTDateTime } from 'src/replicant/utils/time';

export type GiftsSocialFriend = {
  readonly id?: string;
  readonly name?: string;
  readonly photo?: string;
  readonly hasGift?: boolean;
  readonly canSend?: boolean;
  readonly canCollect?: boolean;
};

/**
 * Sort friends by defined rules
 */
export function sortFriendsForGifting(friends: GiftsSocialFriend[]) {
  return friends.sort((a, b) => {
    // Group one
    // There is uncollected energy sent from the friend
    // The player can still send energy to the friend on that day
    const groupOneA = a.hasGift && a.canSend;
    const groupOneB = b.hasGift && b.canSend;
    if (groupOneA !== groupOneB) {
      return groupOneA ? -1 : 1;
    }

    // Group three
    // The player can NOT send energy to the friend on that day any more
    // There is uncollected energy sent from the friend
    const groupThreeA = a.hasGift && !a.canSend;
    const groupThreeB = b.hasGift && !b.canSend;
    if (groupThreeA !== groupThreeB) {
      return groupThreeA ? -1 : 1;
    }

    // Group two
    // The player can still send energy to the friend on that day
    // There is NOT uncollected energy sent from the friend
    const groupTwoA = !a.hasGift && a.canSend;
    const groupTwoB = !b.hasGift && b.canSend;
    if (groupTwoA !== groupTwoB) {
      return groupTwoA ? -1 : 1;
    }

    // Group four
    // The player can NOT send energy to the friend on that day any more
    // There is NOT uncollected energy sent from the friend
    const groupFourA = !a.hasGift && !a.canSend;
    const groupFourB = !b.hasGift && !b.canSend;
    if (groupFourA !== groupFourB) {
      return groupFourA ? -1 : 1;
    }

    return -1;
  });
}

/**
 * Have criteria for unlocking gift feature been met
 */
export const hasMetGiftsUnlockCriteria = (state: State) =>
  isMatch2Available(state);

/**
 * Is gift feature unlocked
 */
export function isGiftsFeatureUnlocked(state: State): boolean {
  return features(state).gifts && hasMetGiftsUnlockCriteria(state);
}

/**
 * Check if we have gifts we can collect now.
 * Gifts held back by the daily cap do not count (#12235).
 */
export function hasGiftsMessages(state: State, now: number): boolean {
  const senderIds = Object.keys(state.gifts.received);
  return getCollectableGiftSenders(state, now, senderIds).length > 0;
}

/**
 * Senders whose gifts can be collected now, oldest gift first.
 * Only `senderIds` are considered, so the popup can pass its friend list.
 *
 * The daily gem cap is enforced here, on the client only (#12235). Gem gifts
 * beyond today's allowance are left out and stay pending.
 */
export function getCollectableGiftSenders(
  state: State,
  now: number,
  senderIds: string[],
): string[] {
  const senders = senderIds
    .filter((id) => Gifts.getters.canCollect(state, id, now))
    .sort(
      (a, b) => state.gifts.received[a].sentAt - state.gifts.received[b].sentAt,
    );

  let gemsLeft = Gifts.getters.getGiftGemsRemainingToday(state, now);

  return senders.filter((id) => {
    const gems = state.gifts.received[id].gems ?? 0;
    if (gems > gemsLeft) return false;

    gemsLeft -= gems;
    return true;
  });
}

/**
 * Time left until the daily gift gem cap resets at 00:00 JST (#12235)
 */
export function getTimeUntilGiftCapReset(now: number): number {
  const resetAt = getJSTDateTime(now).plus({ days: 1 }).startOf('day');
  return resetAt.toMillis() - now;
}
